/**
 * TanStack-Query hook voor `/auth/me`.
 *
 * Houdt de canonieke user-state in de query-cache (deduped, refetchable)
 * en synchroniseert via `onSuccess` met de Zustand-auth-store zodat
 * router-guards + axios-interceptors synchroon kunnen lezen.
 *
 * Patroon dat we voor alle resources volgen (M6):
 *
 *   - TanStack Query bezit "server-state" — cache, dedupe, refetch.
 *   - Zustand bezit "client-state" — synchrone reads vanuit niet-React
 *     code (router-guards, axios-interceptors).
 *   - `onSuccess` koppelt beide zodat het Single-Source-of-Truth
 *     duidelijk blijft (Query) maar Zustand een snapshot heeft.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useAuthStore } from '@shared/stores/auth';
import { useImpersonationStore } from '@shared/stores/impersonation';

export const AUTH_ME_QUERY_KEY = ['auth', 'me'] as const;

export function useAuthMe(enabled: boolean = true) {
  const fetchMe = useAuthStore((s) => s.fetchMe);

  const query = useQuery({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: async () => {
      // Hergebruik de store-action zodat zowel Query-cache als Zustand
      // op één plek worden gevuld. fetchMe geeft de hele auth-me-response
      // terug (data + meta) of null bij 401.
      return await fetchMe();
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });

  // Houd impersonation-store synchroon met de query — fetchMe doet dit
  // intern al, maar voor refetch-triggers (window-focus etc) is dit een
  // veiligheidsnet.
  useEffect(() => {
    if (query.data) {
      useImpersonationStore.getState().hydrateFromMe(query.data);
    }
  }, [query.data]);

  return query;
}
